import { useCallback, useMemo, useRef, useState } from 'react';

import {
  postDocumentacionOtros,
  useGetOtherFiles,
} from '../../../hooks/Reintegro/Documentacion/useDocumentacion';
import { useAlert } from '../../../hooks/UI/useAlert';
import useCameraFilePicker from '../../../hooks/Utils/useCameraFilePicker';
import { stringToFile } from '../../../utils/filesystem';

export type FileType = 'informe' | 'estudio';

export type PendingExtraDocument = {
  id: string;
  type: FileType;
  file: File;
  preview: string;
  mimeType: string | null;
  createdAt: number;
};

export const LABELS: Record<FileType, string> = {
  informe: 'Historia clínica',
  estudio: 'Estudios',
};

interface UseDocumentacionExtraProps {
  siniestroId: string;
  onClose: () => void;
}

const revokePreview = (preview?: string | null): void => {
  if (preview) URL.revokeObjectURL(preview);
};

export const useDocumentacionExtra = ({
  siniestroId,
  onClose,
}: UseDocumentacionExtraProps) => {
  const { alertState, showAlert, hideAlert } = useAlert();

  const [selectedFileType, setSelectedFileType] = useState<FileType | null>(null);
  const selectedFileTypeRef = useRef<FileType | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingExtraDocument[]>([]);
  const [galleryFileId, setGalleryFileId] = useState<string | null>(null);
  const [showGallery, setShowGallery] = useState(false);

  const { mutate: mutateInforme } = useGetOtherFiles(siniestroId, 'informe');
  const { mutate: mutateEstudio } = useGetOtherFiles(siniestroId, 'estudio');

  const selectedGalleryFile = useMemo(
    () => pendingFiles.find((file) => file.id === galleryFileId) ?? null,
    [galleryFileId, pendingFiles],
  );

  const clearPendingFiles = useCallback(() => {
    setPendingFiles((prev) => {
      prev.forEach((item) => revokePreview(item.preview));
      return [];
    });
    setGalleryFileId(null);
  }, []);

  const removePendingFile = useCallback((id: string) => {
    setPendingFiles((prev) => {
      const item = prev.find((file) => file.id === id);
      revokePreview(item?.preview);
      return prev.filter((file) => file.id !== id);
    });
  }, []);

  const handleFileSelected = useCallback(async (file: File | string) => {
    const fileType = selectedFileTypeRef.current;
    if (!fileType) return;

    let finalFile: File;

    if (file instanceof File) {
      finalFile = file;
    } else {
      finalFile = await stringToFile(file, 'documento');
    }

    const preview = URL.createObjectURL(finalFile);

    setPendingFiles((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: fileType,
        file: finalFile,
        preview,
        mimeType: finalFile.type,
        createdAt: Date.now(),
      },
    ]);
    selectedFileTypeRef.current = null;
    setSelectedFileType(null);
  }, []);

  const cameraPicker = useCameraFilePicker({
    onFileSelected: handleFileSelected,
  });

  const selectFileType = useCallback(
    (type: FileType) => {
      selectedFileTypeRef.current = type;
      setSelectedFileType(type);
      cameraPicker.openCameraOptions();
    },
    [cameraPicker],
  );

  const handleFileUploaderClick = useCallback(() => {
    cameraPicker.closeCameraOptions();

    showAlert({
      header: 'Seleccionar tipo',
      message: '¿Qué tipo de documento querés agregar?',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Historia clínica',
          handler: () => selectFileType('informe'),
        },
        {
          text: 'Estudio',
          handler: () => selectFileType('estudio'),
        },
      ],
    });
  }, [cameraPicker, showAlert, selectFileType]);

  const handleUpload = useCallback(async () => {
    if (!pendingFiles.length) return;

    setIsUploading(true);

    const failedIds = new Set<string>();

    for (const pendingFile of pendingFiles) {
      const mutate = pendingFile.type === 'informe' ? mutateInforme : mutateEstudio;
      const uploaded = await postDocumentacionOtros(
        pendingFile.file,
        siniestroId,
        mutate,
        pendingFile.type,
      );

      if (!uploaded) failedIds.add(pendingFile.id);
    }

    setIsUploading(false);

    if (failedIds.size > 0) {
      setPendingFiles((prev) => {
        prev.forEach((item) => {
          if (!failedIds.has(item.id)) revokePreview(item.preview);
        });
        return prev.filter((item) => failedIds.has(item.id));
      });

      showAlert({
        header: 'Error',
        message:
          failedIds.size === pendingFiles.length
            ? 'No se pudieron subir los documentos.'
            : 'Algunos documentos se subieron, pero otros fallaron. Revisá los pendientes e intentá nuevamente.',
        buttons: [{ text: 'Entendido' }],
      });
      return;
    }

    clearPendingFiles();
    showAlert({
      header: 'Documentos subidos',
      message: 'La documentación se cargó correctamente.',
      buttons: [{ text: 'Entendido', handler: onClose }],
    });
  }, [
    pendingFiles,
    siniestroId,
    mutateInforme,
    mutateEstudio,
    showAlert,
    clearPendingFiles,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    showAlert({
      header: 'Cancelar',
      message: 'Si cancelás ahora, los documentos seleccionados se perderán.',
      buttons: [
        { text: 'Volver', role: 'cancel' },
        {
          text: 'Confirmar',
          handler: () => {
            clearPendingFiles();
            onClose();
          },
        },
      ],
    });
  }, [showAlert, clearPendingFiles, onClose]);

  const openGallery = useCallback((id: string) => {
    setGalleryFileId(id);
    setShowGallery(true);
  }, []);

  const closeGallery = useCallback(() => {
    setShowGallery(false);
    setGalleryFileId(null);
  }, []);

  const handleDeleteFromGallery = useCallback(() => {
    if (galleryFileId) removePendingFile(galleryFileId);
    closeGallery();
  }, [galleryFileId, removePendingFile, closeGallery]);

  const firstPendingFile = pendingFiles[0] ?? null;

  return {
    // state
    selectedFileType,
    isUploading,
    pendingFiles,
    pendingCount: pendingFiles.length,
    hasPendingFiles: pendingFiles.length > 0,
    tempFile: firstPendingFile?.file ?? null,
    tempPreview: firstPendingFile?.preview ?? null,
    tempMimeType: firstPendingFile?.mimeType ?? null,
    selectedGalleryFile,
    showGallery,

    // labels
    LABELS,

    // camera
    cameraPicker,

    // alert
    alertState,
    showAlert,
    hideAlert,

    // actions
    selectFileType,
    handleUpload,
    handleCancel,
    openGallery,
    closeGallery,
    handleDeleteFromGallery,
    handleFileUploaderClick,
  };
};
